import TParams from "../@types/api/params"
import { TTicket } from "../@types/data/ticket"
import { TUser } from "../@types/data/user"
import { getAdditionalTax } from "./taxes"
import { getCouponCharge, TCouponCharge } from "./coupon"

type Props = {
  tickets: TTicket[]
  buyer: {
    name: string
    phone: string
  }
  taxTotal: number
  sid: string
  external_reference?: string
  user: TUser
  dk: any
  eventId: string
  eventName: string
  chargeClient: boolean
  // Percentual (escala do adminTaxPercentage) da taxa adicional do PIX.
  // Quando informado, soma a taxa ao transaction_amount enviado ao backend.
  additionalTaxPix?: number | null
  // Pedido com cupom: o valor vem do backend (total do cupom), não da soma
  // local dos ingressos.
  coupon?: Pick<TCouponCharge, "subtotal" | "discountTotal" | "total">
}

export const getOrderData = (
  props: Props
): TParams["get"]["qrcode"]["order"] | null => {
  const {
    tickets,
    buyer,
    taxTotal,
    sid,
    external_reference,
    user,
    dk,
    eventId,
    eventName,
    chargeClient,
    additionalTaxPix,
    coupon,
  } = props

  if (tickets) {
    const ticketsReducedAmount = tickets.reduce(
      (sum, ticket) => sum + +(ticket.price_sell ?? "0"),
      0
    )

    const baseValue = chargeClient
      ? ticketsReducedAmount + taxTotal
      : ticketsReducedAmount

    const paymentValue = coupon
      ? getCouponCharge(coupon, { method: "pix", additionalTaxPix })
      : baseValue +
        getAdditionalTax({
          baseTotal: ticketsReducedAmount,
          method: "pix",
          additionalTaxPix,
        })

    const phone = buyer.phone.replace(/\D/g, "")

    let itemsMap = new Map()

    tickets.forEach((t: any) => {
      if (itemsMap.has(t.id)) {
        itemsMap.set(t.id, {
          ...itemsMap.get(t.id),
          quantity: itemsMap.get(t.id).quantity + 1,
        })
      } else {
        itemsMap.set(t.id, {
          title: t.isMesa
            ? `${t.ticketName} - ${eventName}`
            : `${t.ticketName} - ${t.batchName} - ${eventName}`,
          quantity: 1,
          unit_price: +(t.price_sell ?? "0"),
        })
      }
    })

    let items: any = Array.from(itemsMap.values())

    // Só entra na lista de items quando a taxa foi de fato cobrada do
    // comprador — com chargeClient desligado ela sai do repasse (split), e o
    // transaction_amount acima não a inclui.
    if (chargeClient && taxTotal > 0)
      items.push({
        title: "Taxas",
        quantity: 1,
        unit_amount: taxTotal,
      })

    const firstName = user?.name || (buyer?.name ? buyer.name.trim().split(" ")[0] : "")
    const lastName = user?.username || (buyer?.name ? buyer.name.trim().split(" ").slice(1).join(" ") : "")
    
    const normalize = (str: string) => 
      str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "").toLowerCase()

    const dynamicEmail = firstName 
      ? `${normalize(firstName)}${lastName ? `_${normalize(lastName)}` : ""}@email.com` 
      : "email@email.com"

    const obj: TParams["get"]["qrcode"]["order"] = {
      external_reference: external_reference ?? "",
      transaction_amount: paymentValue,
      payment_method_id: "pix",
      payer: {
        first_name: firstName,
        last_name: lastName,
        email: dynamicEmail,
      },
      metadata: {
        socketId: sid,
        payer: {
          phone: {
            area_code: phone.slice(0, 2),
            number: phone.slice(2),
          },
        },
        cCode: sid,
        dk,
        eventId,
      },
      items,
    }

    return obj
  } else return null
}
